/**
 * Unsaved-changes guard — context + provider + hook (E10-F02-T02).
 *
 * Parity with the legacy `dashboard.js → showUnsavedDialog(onProceed)` wired into
 * `core.js → navigate()`. Before any navigation (or close) the live app called
 * `hasUnsavedChanges()`: clean → proceed immediately; dirty → open the
 * `<dialog id="unsaved-dialog">` and defer the navigation behind one of three
 * buttons (Save / Discard / Cancel).
 *
 * Design here:
 *   - {@link UnsavedGuardProvider} holds EVERY registered draft source (the open
 *     project-detail card registers its header edits, the Add-Information panel
 *     registers its context/files draft) and a single deferred callback; it
 *     renders the {@link UnsavedDialog}.
 *   - {@link useUnsavedGuard} returns `guard(onProceed)`. Callers wrap navigation:
 *     `guard(() => navigate(route))`.
 *   - `useRegisterUnsavedSource(source)` lets the active editor publish its draft
 *     + Save/Discard logic to the provider for the lifetime of the mount.
 *
 * E02-F03-T08 — the two wiring points that make this guard REAL (before them the
 * whole module was dead code exercised only by its own unit tests):
 *   1. `shell/AppShell` routes EVERY in-app navigation (sidebar + view-level
 *      `navigate`) through `guard(...)`, so leaving a dirty project card opens
 *      the dialog instead of silently dropping the edits;
 *   2. the provider owns a `beforeunload` listener (legacy parity —
 *      `../EER app/public/assets/js/inbox.js` `window.onbeforeunload`), so a tab
 *      close / F5 with a dirty draft asks the browser's native confirm.
 *
 * MULTIPLE sources are supported (one card can have several independent editors:
 * the header bag AND the Add-Information draft). `guard` blocks when ANY of them
 * is dirty; Save/Discard fan out to the dirty ones only, and the deferred
 * navigation runs after ALL saves resolve (any rejection keeps the dialog open).
 *
 * Critical acceptance rule — "если диалог отсутствует в DOM, callback вызывается
 * напрямую (без блокировки)": this is the DEFAULT context value. When no
 * provider is mounted (or no source is registered, or every draft is clean),
 * `guard(cb)` simply runs `cb()` synchronously — no dialog, no block. The dialog
 * is mounted only while a dirty navigation is actually pending.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { UnsavedDialog } from './UnsavedDialog.js';
import { hasUnsavedChanges, type UnsavedDraft } from './unsaved.js';

/**
 * The contract an active editor (e.g. the project-detail card) registers so the
 * guard can read its dirty state and perform Save/Discard on its behalf.
 */
export interface UnsavedSource {
  /** Snapshot the current draft. Called at guard time to evaluate dirtiness. */
  readonly getDraft: () => UnsavedDraft;
  /**
   * Persist the staged changes (legacy `saveProjectChanges`). May be async; the
   * deferred navigation runs only after it resolves. A rejection keeps the
   * dialog open (the navigation is NOT performed).
   */
  readonly onSave: () => void | Promise<void>;
  /**
   * Drop the draft (legacy `clearDraft`: reset `unsavedUpdates`, close edit
   * fields, clear `pendingFiles`, wipe the localStorage draft). Called before a
   * Discard-and-exit navigation.
   */
  readonly onDiscard: () => void;
}

export interface UnsavedGuardApi {
  /**
   * Run `onProceed`, gated by unsaved changes:
   *   - clean (or no source registered) → `onProceed()` runs synchronously;
   *   - dirty → open the unsaved dialog and defer `onProceed` behind it.
   */
  readonly guard: (onProceed: () => void) => void;
}

interface UnsavedGuardInternal extends UnsavedGuardApi {
  readonly register: (source: UnsavedSource) => () => void;
}

/**
 * Default value: NO provider. `guard` runs the callback directly (the dialog is
 * absent from the DOM), and `register` is a no-op. This is what makes the guard
 * safe to call from anywhere — an un-wrapped navigation just proceeds.
 */
const DEFAULT_GUARD: UnsavedGuardInternal = {
  guard: (onProceed) => onProceed(),
  register: () => () => {},
};

const UnsavedGuardContext = createContext<UnsavedGuardInternal>(DEFAULT_GUARD);

export interface UnsavedGuardProviderProps {
  readonly children: ReactNode;
}

export function UnsavedGuardProvider({ children }: UnsavedGuardProviderProps): React.JSX.Element {
  // Every registered editor source. A ref (not state) so `guard` reads the
  // freshest set without re-creating the callback — and so registering during an
  // effect never re-renders the whole shell.
  const sourcesRef = useRef<Set<UnsavedSource>>(new Set());
  // The navigation deferred behind the dialog. Held in a ref (it is invoked, not
  // rendered) and mirrored into state only to drive open/close of the dialog.
  const pendingProceed = useRef<(() => void) | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const register = useCallback((source: UnsavedSource): (() => void) => {
    sourcesRef.current.add(source);
    // Unmount → drop exactly this source; the others keep guarding.
    return () => {
      sourcesRef.current.delete(source);
    };
  }, []);

  /** The registered sources whose draft is currently dirty (possibly none). */
  const dirtySources = useCallback(
    (): UnsavedSource[] =>
      [...sourcesRef.current].filter((source) => hasUnsavedChanges(source.getDraft())),
    [],
  );

  const guard = useCallback(
    (onProceed: () => void): void => {
      // No active editor, or every draft is clean → proceed immediately.
      if (dirtySources().length === 0) {
        onProceed();
        return;
      }
      // Dirty → defer the navigation behind the dialog.
      pendingProceed.current = onProceed;
      setOpen(true);
    },
    [dirtySources],
  );

  // Legacy parity (`window.onbeforeunload` in the v1 SPA): a tab close / reload /
  // external link with a dirty draft raises the browser's own confirm. The in-app
  // dialog cannot be shown for a real unload, so this is the only guard there.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (dirtySources().length === 0) return;
      event.preventDefault();
      // Legacy browsers key off a non-empty returnValue; modern ones ignore the text.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirtySources]);

  // ---- dialog actions -----------------------------------------------------

  const closeDialog = useCallback((): void => {
    setOpen(false);
    setSaving(false);
    pendingProceed.current = null;
  }, []);

  const runPending = useCallback((): void => {
    const proceed = pendingProceed.current;
    pendingProceed.current = null;
    setOpen(false);
    setSaving(false);
    proceed?.();
  }, []);

  const handleSave = useCallback((): void => {
    const sources = dirtySources();
    if (sources.length === 0) {
      runPending();
      return;
    }
    const inFlight: Promise<void>[] = [];
    try {
      for (const source of sources) {
        const result = source.onSave();
        if (result instanceof Promise) inFlight.push(result);
      }
    } catch {
      // Synchronous save failure → keep the dialog open, abort navigation.
      setSaving(false);
      return;
    }
    if (inFlight.length > 0) {
      setSaving(true);
      // ALL saves must land before the deferred navigation runs; one rejection
      // keeps the dialog open (the editor owns its own error toast).
      void Promise.all(inFlight).then(
        () => runPending(),
        () => setSaving(false),
      );
      return;
    }
    runPending();
  }, [dirtySources, runPending]);

  const handleDiscard = useCallback((): void => {
    for (const source of dirtySources()) {
      source.onDiscard();
    }
    runPending();
  }, [dirtySources, runPending]);

  // Cancel = stay: close the dialog, keep the draft and the registered source.
  const handleCancel = closeDialog;

  const api = useMemo<UnsavedGuardInternal>(() => ({ guard, register }), [guard, register]);

  return (
    <UnsavedGuardContext.Provider value={api}>
      {children}
      <UnsavedDialog
        open={open}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onCancel={handleCancel}
      />
    </UnsavedGuardContext.Provider>
  );
}

/** Access the guard. Outside a provider this returns the direct-proceed default. */
export function useUnsavedGuard(): UnsavedGuardApi {
  const { guard } = useContext(UnsavedGuardContext);
  return useMemo(() => ({ guard }), [guard]);
}

/**
 * Register the active editor's draft source for the lifetime of the mount.
 * The project-detail card calls this with its `getDraft` + Save/Discard handlers;
 * on unmount the source is automatically cleared so a navigated-away card never
 * keeps blocking. Outside a provider this is an inert no-op.
 */
export function useRegisterUnsavedSource(source: UnsavedSource): void {
  const { register } = useContext(UnsavedGuardContext);
  // Keep the latest handlers without re-registering on every render: stash them
  // in a ref and register a stable wrapper once per mount.
  const latest = useRef(source);
  latest.current = source;

  useEffect(() => {
    const unregister = register({
      getDraft: () => latest.current.getDraft(),
      onSave: () => latest.current.onSave(),
      onDiscard: () => latest.current.onDiscard(),
    });
    return unregister;
  }, [register]);
}
